/**
 * One estimate (tracker item 39): what was quoted, its history, and the
 * office actions its status allows: edit (DRAFT), send / resend (the email
 * carries a view-and-approve link; an expired one needs a new date), record
 * the customer's answer given by phone or in person, cancel, print, and
 * convert an approved estimate to a draft job or a recurring schedule (one
 * transaction on the server: nothing is created if any part fails).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { createAPIClient, IEstimatesApi, IUsersApi } from '../../api';
import * as types from '../../api/types';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';
import { getErrorMessage } from '../../utils/apiError';
import { invoiceDate, invoiceMoney } from '../invoiceUi';
import {
  ESTIMATES_PATH, ESTIMATE_STATUS_LABELS, RESPONSE_CHANNEL_LABELS, bundleDayLabel, bundleStartRequired, estimateActions, estimateAddress, estimateEditPath,
  estimateErrorText, estimatePrintPath, estimateStatusClass, isStaleEstimateError, localDatePlus, newIdempotencyKey,
} from './estimateUi';
import { enrollmentPath } from '../../features/service-bundles/bundleUi';
import { formatJobDate } from '../../utils/jobPresentation';

type Api = IEstimatesApi & IUsersApi;
type Panel = null | 'send' | 'approve' | 'decline' | 'cancel' | 'convert';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
type Frequency = NonNullable<types.EstimateConvertInput['recurring']>['frequency'];
const FREQUENCIES: Frequency[] = ['DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY', 'ANNUALLY'];

function stamp(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

function LinesTable({ lines, hasDiscounts, label, showDay = false }: { lines: types.EstimateLine[]; hasDiscounts: boolean; label: string; showDay?: boolean }) {
  return (
    <div className="overflow-x-auto">
      <table className="responsive-table w-full text-sm" aria-label={label}>
        <thead>
          <tr className="border-b">
            {['Description', 'Qty', 'Unit price', 'Amount', ...(hasDiscounts ? ['Discount'] : []), 'Tax', 'Total'].map((h, i) => (
              <th key={h} className={`px-2 py-2 font-semibold ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id} className="border-b border-gray-100">
              <td className="px-2 py-2" data-label="Description" data-primary>
                {showDay && <span className="mr-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-900" data-testid="estimate-line-day">{bundleDayLabel(line.bundleOffsetDays)}</span>}
                {line.description}
                {line.description !== line.itemName && <span className="block text-xs text-gray-500">{line.itemName}</span>}
              </td>
              <td className="px-2 py-2 text-right" data-label="Qty">{line.quantity}</td>
              <td className="px-2 py-2 text-right" data-label="Unit price">{invoiceMoney(line.unitPrice)}{line.priceSource === 'MANUAL' ? ' (manual)' : ''}</td>
              <td className="px-2 py-2 text-right" data-label="Amount">{invoiceMoney(line.lineAmount)}</td>
              {hasDiscounts && <td className="px-2 py-2 text-right" data-label="Discount">{Number(line.lineDiscount) ? `−${invoiceMoney(line.lineDiscount)}` : '—'}</td>}
              <td className="px-2 py-2 text-right" data-label="Tax">{invoiceMoney(line.lineTax)}</td>
              <td className="px-2 py-2 text-right font-semibold" data-label="Total">{invoiceMoney(line.lineTotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function EstimateDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const api = useMemo<Api>(() => createAPIClient(), []);
  const [estimate, setEstimate] = useState<types.Estimate | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [assignees, setAssignees] = useState<types.AssignableUser[]>([]);

  // Panel fields
  const [sendExpiry, setSendExpiry] = useState('');
  const [sendKey, setSendKey] = useState(newIdempotencyKey());
  const [responder, setResponder] = useState('');
  const [comment, setComment] = useState('');
  const [reason, setReason] = useState('');
  const [target, setTarget] = useState<types.EstimateConversionTarget>('JOB');
  const [jobDate, setJobDate] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [frequency, setFrequency] = useState<Frequency>('WEEKLY');
  const [interval, setIntervalValue] = useState('1');
  const [days, setDays] = useState<number[]>([1]);
  const [startDate, setStartDate] = useState(localDatePlus(7));
  const [endDate, setEndDate] = useState('');
  // Bundle lines (tracker item 85): the date the first remaining visit falls on, asked when a bundle has none
  const [bundleStart, setBundleStart] = useState('');
  const [startNeeded, setStartNeeded] = useState<string[] | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setEstimate(await api.getEstimate(id));
    } catch (cause) {
      setEstimate(null);
      setLoadError(getErrorMessage(cause, 'Could not load the estimate'));
    }
  }, [api, id]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (panel !== 'convert' || assignees.length > 0) return;
    api.listAssignableUsers().then(setAssignees).catch(() => setAssignees([]));
  }, [api, panel, assignees.length]);

  const open = (next: Panel) => {
    setPanel(next);
    setError(null);
    setNotice(null);
    if (next === 'convert') { setStartNeeded(null); setBundleStart(''); }
    if (next === 'send') {
      setSendKey(newIdempotencyKey());
      setSendExpiry(estimate && estimate.status === 'EXPIRED' ? localDatePlus(30) : '');
    }
  };

  async function act(work: () => Promise<void | false>, fallback: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      // A work step that answers false keeps the panel open (the start date prompt)
      if ((await work()) !== false) setPanel(null);
    } catch (cause) {
      setError(estimateErrorText(cause, fallback));
      if (isStaleEstimateError(cause)) await load();
    } finally {
      setBusy(false);
    }
  }

  const send = () => act(async () => {
    const result = await api.sendEstimate(id, { idempotencyKey: sendKey, ...(sendExpiry ? { expiryDate: sendExpiry } : {}) });
    setEstimate(result.estimate);
    if (!result.created) setNotice('This send was already made: no second email was queued.');
    else if (result.message.status === 'SKIPPED') setNotice(`The estimate is marked sent, but the email was not sent: ${result.message.skipReasonText ?? result.message.skipReason}. Print it or send it another way.`);
    else setNotice(`The estimate was emailed to ${result.message.toAddress}.`);
  }, 'Could not send the estimate');

  const record = (decision: types.EstimateDecision) => act(async () => {
    const saved = await api.recordEstimateResponse(id, decision, { name: responder.trim() || null, comment: comment.trim() || null });
    setEstimate(saved);
    setNotice(decision === 'APPROVED' ? 'Recorded as approved. You can now convert it to a job or a recurring schedule.' : 'Recorded as declined.');
  }, 'Could not record the answer');

  const cancel = () => act(async () => {
    setEstimate(await api.cancelEstimate(id, reason.trim() || null));
    setNotice('The estimate was cancelled. Its links no longer work.');
  }, 'Could not cancel the estimate');

  const plainLines = estimate ? estimate.lineItems.filter((l) => l.bundleOffsetDays === null || l.bundleOffsetDays === undefined) : [];
  const bundleOnly = !!estimate && estimate.bundles.length > 0 && plainLines.length === 0;
  const effectiveTarget: types.EstimateConversionTarget = bundleOnly ? 'JOB' : target;

  const convertProblem = effectiveTarget === 'RECURRING'
    ? (!assignedTo ? 'Choose who the recurring visits are assigned to' : !startDate ? 'Choose the start date' : frequency === 'WEEKLY' && days.length === 0 ? 'Choose at least one weekday' : null)
    : null;

  const convert = () => {
    if (convertProblem) { setError(convertProblem); return; }
    return act(async () => {
      const body: types.EstimateConvertInput = effectiveTarget === 'JOB'
        ? { target: 'JOB', job: { scheduledDate: bundleOnly ? null : jobDate || null, assignedTo: assignedTo || null } }
        : {
            target: 'RECURRING',
            recurring: {
              frequency,
              interval: Math.max(1, Number(interval) || 1),
              ...(frequency === 'WEEKLY' ? { frequencyDetails: { daysOfWeek: [...days].sort() } } : {}),
              startDate,
              endDate: endDate || null,
              assignedTo,
            },
          };
      if (bundleStart) body.startDate = bundleStart;
      let result: types.EstimateConversionResult;
      try {
        result = await api.convertEstimate(id, body);
      } catch (cause) {
        // A bundle without a start date: ask for it here and send the conversion again (nothing was created)
        const needed = bundleStartRequired(cause);
        if (!needed) throw cause;
        setStartNeeded(needed.map((b) => b.bundleName));
        return false;
      }
      setStartNeeded(null);
      setEstimate(result.estimate);
      const made = (result.enrollmentIds ?? []).length;
      const parts = [
        ...(made > 0 ? [`${made} bundle enrollment${made === 1 ? ' was' : 's were'} created`] : []),
        ...(result.scheduleId ? ['a recurring schedule was created'] : result.jobId ? ['a draft job was created'] : []),
      ];
      const sentence = parts.join(' and ');
      setNotice(`${sentence.charAt(0).toUpperCase()}${sentence.slice(1)} from this estimate.`);
    }, 'Could not convert the estimate');
  };

  if (!estimate) {
    return (
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <Link to={ESTIMATES_PATH} className="text-sm text-blue-700 hover:underline">← Estimates</Link>
        {loadError ? <p role="alert" className="mt-4 rounded bg-red-50 p-3 text-red-800">{loadError}</p> : <p role="status" className="mt-4">Loading estimate…</p>}
      </main>
    );
  }

  const actions = estimateActions(estimate.status);
  const hasDiscounts = estimate.discounts.length > 0;
  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <Link to={ESTIMATES_PATH} className="text-sm text-blue-700 hover:underline">← Estimates</Link>
      <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="break-words text-2xl font-bold text-gray-900">Estimate {estimate.estimateNumber}</h1>
          {estimate.title && <p className="text-gray-700">{estimate.title}</p>}
        </div>
        <span className={`rounded px-2 py-0.5 text-xs font-semibold ${estimateStatusClass(estimate.status)}`} data-testid="estimate-status">
          {ESTIMATE_STATUS_LABELS[estimate.status]}
        </span>
      </div>
      {notice && <Toast kind="success">{notice}</Toast>}
      {error && <p role="alert" className="mt-3 rounded bg-red-50 p-3 text-sm text-red-800">{error}</p>}

      <div className="mt-4 flex flex-wrap gap-2" aria-label="Estimate actions">
        {actions.edit && <Link to={estimateEditPath(estimate.id)} className={secondaryButtonClass}>Edit</Link>}
        {(actions.send || actions.resend) && <button type="button" className={primaryButtonClass} onClick={() => open('send')}>{actions.send ? 'Send' : 'Resend'}</button>}
        {actions.record && <button type="button" className={secondaryButtonClass} onClick={() => open('approve')}>Record approval</button>}
        {actions.record && <button type="button" className={secondaryButtonClass} onClick={() => open('decline')}>Record decline</button>}
        {actions.convert && <button type="button" className={primaryButtonClass} onClick={() => open('convert')}>Convert</button>}
        {actions.cancel && <button type="button" className={secondaryButtonClass} onClick={() => open('cancel')}>Cancel estimate</button>}
        <Link to={estimatePrintPath(estimate.id)} className={secondaryButtonClass}>Print</Link>
      </div>

      {panel === 'send' && (
        <section aria-label="Send estimate" className="mt-4 rounded-lg bg-white p-4 shadow">
          <p className="text-sm text-gray-700">
            The customer ({estimate.customer.email ?? 'no email on file'}) receives an email with a link to view and approve or decline the estimate.
          </p>
          <div className="mt-3 max-w-xs">
            <label htmlFor="send-expiry" className={labelClass}>{estimate.status === 'EXPIRED' ? 'New expiry date' : `Valid until (now ${invoiceDate(estimate.expiryDate)})`}</label>
            <input id="send-expiry" type="date" className={inputClass} min={localDatePlus(0)} value={sendExpiry} onChange={(e) => setSendExpiry(e.target.value)} />
          </div>
          <div className="mt-3 flex gap-2">
            <button type="button" className={primaryButtonClass} disabled={busy} onClick={() => void send()}>{busy ? 'Sending…' : 'Send now'}</button>
            <button type="button" className={secondaryButtonClass} onClick={() => setPanel(null)}>Close</button>
          </div>
        </section>
      )}

      {(panel === 'approve' || panel === 'decline') && (
        <section aria-label={panel === 'approve' ? 'Record approval' : 'Record decline'} className="mt-4 rounded-lg bg-white p-4 shadow">
          <p className="text-sm text-gray-700">Record the customer's answer given by phone, email or in person.</p>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="responder" className={labelClass}>Who answered (optional)</label>
              <input id="responder" className={inputClass} maxLength={200} value={responder} onChange={(e) => setResponder(e.target.value)} />
            </div>
            <div>
              <label htmlFor="response-comment" className={labelClass}>{panel === 'approve' ? 'Note (optional)' : 'Reason (optional)'}</label>
              <input id="response-comment" className={inputClass} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button type="button" className={primaryButtonClass} disabled={busy} onClick={() => void record(panel === 'approve' ? 'APPROVED' : 'DECLINED')}>
              {panel === 'approve' ? 'Record approval' : 'Record decline'}
            </button>
            <button type="button" className={secondaryButtonClass} onClick={() => setPanel(null)}>Close</button>
          </div>
        </section>
      )}

      {panel === 'cancel' && (
        <section aria-label="Cancel estimate" className="mt-4 rounded-lg bg-white p-4 shadow">
          <label htmlFor="cancel-reason" className={labelClass}>Reason (optional, staff only)</label>
          <input id="cancel-reason" className={inputClass} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
          <div className="mt-3 flex gap-2">
            <button type="button" className={primaryButtonClass} disabled={busy} onClick={() => void cancel()}>Cancel estimate</button>
            <button type="button" className={secondaryButtonClass} onClick={() => setPanel(null)}>Keep it</button>
          </div>
        </section>
      )}

      {panel === 'convert' && (
        <section aria-label="Convert estimate" className="mt-4 rounded-lg bg-white p-4 shadow">
          {estimate.bundles.length > 0 && (
            <p className="mb-3 rounded bg-blue-50 p-2 text-sm text-blue-900" data-testid="convert-bundle-note">
              {estimate.bundles.map((b) => b.bundleName).join(', ')} {estimate.bundles.length === 1 ? 'becomes' : 'become'} a bundle enrollment with one visit job per line, at the quoted prices.
              {bundleOnly ? ' This estimate has only bundle lines, so no separate job is created.' : ' The other lines convert as below.'}
            </p>
          )}
          {!bundleOnly && (
            <fieldset>
              <legend className="text-sm font-semibold text-gray-700">Convert to</legend>
              <label className="mt-1 flex items-center gap-2 text-sm">
                <input type="radio" name="target" checked={target === 'JOB'} onChange={() => setTarget('JOB')} /> A draft job (one visit)
              </label>
              <label className="mt-1 flex items-center gap-2 text-sm">
                <input type="radio" name="target" checked={target === 'RECURRING'} onChange={() => setTarget('RECURRING')} /> A recurring schedule
              </label>
            </fieldset>
          )}
          {startNeeded && (
            <div className="mt-3 rounded border border-amber-300 bg-amber-50 p-3" role="group" aria-label="Bundle start date" data-testid="bundle-start-prompt">
              <p className="text-sm font-semibold text-amber-900">When should {startNeeded.join(', ')} start?</p>
              <p className="mt-1 text-xs text-amber-900">The first visit falls on this date and the other visits keep their gaps.</p>
              <div className="mt-2 max-w-xs">
                <label htmlFor="convert-bundle-start" className={labelClass}>Bundle start date</label>
                <input id="convert-bundle-start" type="date" className={inputClass} value={bundleStart} onChange={(e) => setBundleStart(e.target.value)} />
              </div>
            </div>
          )}
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="convert-assignee" className={labelClass}>{effectiveTarget === 'RECURRING' ? 'Assigned to' : 'Assigned to (optional)'}</label>
              <select id="convert-assignee" className={inputClass} value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
                <option value="">{effectiveTarget === 'RECURRING' ? 'Choose a person' : 'Nobody yet'}</option>
                {assignees.map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
              </select>
            </div>
            {effectiveTarget === 'JOB' ? (
              bundleOnly ? null : (
                <div>
                  <label htmlFor="convert-date" className={labelClass}>Date (optional)</label>
                  <input id="convert-date" type="date" className={inputClass} value={jobDate} onChange={(e) => setJobDate(e.target.value)} />
                </div>
              )
            ) : (
              <>
                <div>
                  <label htmlFor="convert-frequency" className={labelClass}>Repeats</label>
                  <select id="convert-frequency" className={inputClass} value={frequency} onChange={(e) => setFrequency(e.target.value as Frequency)}>
                    {FREQUENCIES.map((f) => <option key={f} value={f}>{f.charAt(0) + f.slice(1).toLowerCase()}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="convert-interval" className={labelClass}>Every</label>
                  <input id="convert-interval" className={inputClass} inputMode="numeric" value={interval} onChange={(e) => setIntervalValue(e.target.value)} />
                </div>
                {frequency === 'WEEKLY' && (
                  <fieldset>
                    <legend className={labelClass}>On</legend>
                    <div className="flex flex-wrap gap-2">
                      {WEEKDAYS.map((label, day) => (
                        <label key={label} className="flex items-center gap-1 text-sm">
                          <input type="checkbox" checked={days.includes(day)} onChange={(e) => setDays((current) => (e.target.checked ? [...current, day] : current.filter((d) => d !== day)))} />
                          {label}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                )}
                <div>
                  <label htmlFor="convert-start" className={labelClass}>Starts</label>
                  <input id="convert-start" type="date" className={inputClass} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </div>
                <div>
                  <label htmlFor="convert-end" className={labelClass}>Ends (optional)</label>
                  <input id="convert-end" type="date" className={inputClass} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>
              </>
            )}
          </div>
          <p className="mt-3 text-xs text-gray-600">
            The lines keep the quoted prices. Discounts are applied again when the work is invoiced.
          </p>
          <div className="mt-3 flex gap-2">
            <button type="button" className={primaryButtonClass} disabled={busy} onClick={() => void convert()}>{busy ? 'Converting…' : startNeeded ? 'Convert with this date' : 'Convert'}</button>
            <button type="button" className={secondaryButtonClass} onClick={() => setPanel(null)}>Close</button>
          </div>
        </section>
      )}

      <section aria-labelledby="estimate-summary" className="mt-4 rounded-lg bg-white p-4 shadow sm:p-5">
        <h2 id="estimate-summary" className="mb-3 text-lg font-semibold">Summary</h2>
        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div><dt className="text-gray-500">Customer</dt><dd><Link to={`/customers/${estimate.customer.id}`} className="text-blue-700 hover:underline">{estimate.customer.name}</Link></dd></div>
          <div><dt className="text-gray-500">Property</dt><dd className="break-words">{estimateAddress(estimate.property)}</dd></div>
          <div><dt className="text-gray-500">Valid until</dt><dd>{invoiceDate(estimate.expiryDate)}</dd></div>
          <div><dt className="text-gray-500">Sent</dt><dd>{estimate.sentAt ? `${stamp(estimate.lastSentAt)}${estimate.sendCount > 1 ? ` (${estimate.sendCount} times)` : ''}` : 'Not yet'}</dd></div>
          {estimate.respondedAt && (
            <div className="sm:col-span-2">
              <dt className="text-gray-500">Answer</dt>
              <dd data-testid="estimate-answer">
                {estimate.status === 'DECLINED' ? 'Declined' : 'Approved'} {stamp(estimate.respondedAt)}
                {estimate.responderName ? ` by ${estimate.responderName}` : ''}
                {estimate.respondedVia ? ` through ${RESPONSE_CHANNEL_LABELS[estimate.respondedVia]}` : ''}
                {estimate.responseComment && <span className="block text-gray-700">“{estimate.responseComment}”</span>}
              </dd>
            </div>
          )}
          {(estimate.convertedJobId || (estimate.convertedEnrollmentIds ?? []).length > 0) && (
            <div className="sm:col-span-2">
              <dt className="text-gray-500">Converted</dt>
              <dd data-testid="estimate-converted">
                {estimate.convertedJobId && (
                  <>
                    {estimate.convertedScheduleId
                      ? <Link to={`/recurring/${estimate.convertedScheduleId}`} className="text-blue-700 hover:underline">Recurring schedule</Link>
                      : <Link to={`/jobs/${estimate.convertedJobId}`} className="text-blue-700 hover:underline">Draft job</Link>}
                    {estimate.convertedScheduleId && <> · <Link to={`/jobs/${estimate.convertedJobId}`} className="text-blue-700 hover:underline">template job</Link></>}
                  </>
                )}
                {(estimate.convertedEnrollmentIds ?? []).map((enrollmentId, index) => (
                  <React.Fragment key={enrollmentId}>
                    {(estimate.convertedJobId || index > 0) && ' · '}
                    <Link to={enrollmentPath(enrollmentId)} className="text-blue-700 hover:underline" data-testid="converted-enrollment-link">
                      {(estimate.convertedEnrollmentIds ?? []).length > 1 ? `Bundle enrollment ${index + 1}` : 'Bundle enrollment'}
                    </Link>
                  </React.Fragment>
                ))}
              </dd>
            </div>
          )}
          {estimate.cancelledAt && <div className="sm:col-span-2"><dt className="text-gray-500">Cancelled</dt><dd>{stamp(estimate.cancelledAt)}{estimate.cancelReason ? `: ${estimate.cancelReason}` : ''}</dd></div>}
          {estimate.customerMessage && <div className="sm:col-span-2"><dt className="text-gray-500">Message to the customer</dt><dd className="whitespace-pre-wrap break-words">{estimate.customerMessage}</dd></div>}
          {estimate.internalNotes && <div className="sm:col-span-2"><dt className="text-gray-500">Internal notes</dt><dd className="whitespace-pre-wrap break-words">{estimate.internalNotes}</dd></div>}
        </dl>
      </section>

      <section aria-labelledby="estimate-lines-heading" className="mt-4 rounded-lg bg-white p-4 shadow sm:p-5">
        <h2 id="estimate-lines-heading" className="mb-3 text-lg font-semibold">Lines</h2>
        {estimate.lineItems.length === 0 ? <p className="text-sm text-gray-600">No lines yet. Edit the estimate to add some.</p> : (
          <>
            {(() => {
              const grouped = new Set(estimate.bundles.flatMap((g) => g.lineIds));
              const plain = estimate.lineItems.filter((l) => !grouped.has(l.id));
              return (
                <>
                  {plain.length > 0 && <LinesTable lines={plain} hasDiscounts={hasDiscounts} label="Estimate lines" />}
                  {estimate.bundles.map((group) => (
                    <div key={group.bundleId ?? `name:${group.bundleName}`} className="mt-4 rounded border border-blue-200" role="group" aria-label={`Bundle ${group.bundleName}`} data-testid="estimate-bundle">
                      <div className="flex flex-wrap items-center justify-between gap-2 rounded-t bg-blue-50 px-3 py-2">
                        <h3 className="font-semibold text-blue-900">{group.bundleName}</h3>
                        <span className="text-sm text-blue-900" data-testid="estimate-bundle-start">
                          {group.startDate ? `Starts ${formatJobDate(group.startDate)}` : 'Start date asked when converted'}
                        </span>
                        <span className="text-sm text-blue-900" data-testid="estimate-bundle-totals">
                          Subtotal {invoiceMoney(group.subtotal)} · Tax {invoiceMoney(group.totalTax)} · <strong>Total {invoiceMoney(group.total)}</strong>
                        </span>
                      </div>
                      <div className="p-2">
                        <LinesTable lines={estimate.lineItems.filter((l) => group.lineIds.includes(l.id))} hasDiscounts={hasDiscounts} label={`${group.bundleName} visits`} showDay />
                      </div>
                    </div>
                  ))}
                </>
              );
            })()}
          </>
        )}
        <dl className="mt-4 ml-auto max-w-xs space-y-1 text-sm" aria-label="Estimate totals">
          <div className="flex justify-between gap-6"><dt>Subtotal</dt><dd>{invoiceMoney(estimate.subtotal)}</dd></div>
          {estimate.discounts.map((d, i) => <div key={i} className="flex justify-between gap-6"><dt>{d.name}</dt><dd>−{invoiceMoney(d.amount)}</dd></div>)}
          <div className="flex justify-between gap-6"><dt>Tax</dt><dd>{invoiceMoney(estimate.totalTax)}</dd></div>
          <div className="flex justify-between gap-6 border-t pt-1 text-base font-bold"><dt>Total</dt><dd data-testid="estimate-total">{invoiceMoney(estimate.total)}</dd></div>
        </dl>
      </section>
    </main>
  );
}
